import React from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import PlusIcon from '@assets/PlusIcon.svg'
import MessageIcon from '@assets/MessageIcon.svg'
import NotificationIcon from '@/assets/ConsumersNotifications/TextDetailsIcon.png'
import { useAppTheme } from '@/theme/ThemeContext'
import NotificationStatusChip from '@/components/notification-send/NotificationStatusChip'
import type { MessageItem } from '@/mockdata/messagesByUser'

type NotificationsPanelProps = {
  selectedRow?: any | null
  items?: MessageItem[]
  loading?: boolean

  onAdd?: () => void
  enableAdd?: boolean
  width?: number | string
  sx?: SxProps<Theme>

  emptyTitle?: string
  emptyLines?: string[]
  emptyIcon?: React.ReactNode
  emptyIconSrc?: string
  iconSize?: number
  iconAlt?: string
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  selectedRow = null,
  items = [],
  loading = false,
  onAdd,
  enableAdd = false,
  width = 320,
  sx,

  emptyTitle = 'פרטי הודעה',
  emptyLines = [
    'לא נבחרה התראה.',
    'לתצוגה של פרטי הודעה ללקוח יש ללחוץ על שורת התראה',
  ],
  emptyIcon,
  emptyIconSrc,
  iconSize = 40,
  iconAlt = 'notification icon',
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    text: isDarkMode ? '#fff' : '#003F61',
    secondaryText: isDarkMode ? '#93A3B5' : '#9AA4B2',
    mutedText: isDarkMode ? '#6B7A8C' : '#8F98A5',
    border: isDarkMode ? '#445876' : '#E3E8EF',
    divider: isDarkMode ? '#445876' : '#EDF2F7',
    btnBg: isDarkMode ? '#202B40' : '#fff',
    btnHover: isDarkMode ? '#34507D' : '#F7FAFC',
    btnColor: isDarkMode ? '#81B5FF' : '#2F6BB3',
    card: isDarkMode ? '#0D1A33' : '#fff',
    scrollTrack: isDarkMode ? '#202B40' : '#F1F5F9',
    scrollThumb: isDarkMode ? '#445876' : '#CBD5E1',
    scrollThumbHover: isDarkMode ? '#81B5FF' : '#94A3B8',
  }

  const HeaderButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        color: colors.btnColor,
        bgcolor: colors.btnBg,
        cursor: onClick ? 'pointer' : 'not-allowed',
        '&:hover': { bgcolor: colors.btnBg },
      }}
    >
      <Typography component="span" sx={{ fontFamily: 'Rubik, sans-serif' }}>
        <img
          src={PlusIcon}
          alt="add"
          style={{ filter: isDarkMode ? 'invert(1) brightness(0.6)' : 'none' }}
        />
      </Typography>
    </IconButton>
  )

  const count = items.length

  const renderEmptyIcon = () => {
    if (emptyIcon) return <Box sx={{ mb: 1.5 }}>{emptyIcon}</Box>
    const src = emptyIconSrc || NotificationIcon
    const size = iconSize
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box
          component="img"
          src={src}
          alt={iconAlt}
          loading="lazy"
          sx={{
            width: size,
            height: size,
            objectFit: 'contain',
            display: 'block',
            mx: 'auto',
            filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
          }}
        />
      </Box>
    )
  }

  const hasSelection = Boolean(selectedRow)

  return (
    <Paper
      elevation={0}
      sx={{
        flex: `0 0 ${typeof width === 'number' ? `${width}px` : width}`,
        maxWidth: width,
        px: 1,
        pt: 2,
        height: '100%',
        display: 'flex',
        bgcolor: 'transparent',
        boxShadow: 'none',
        flexDirection: 'column',
        direction: 'rtl',
        ...sx,
      }}
    >
      {!hasSelection && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'center',
            color: 'text.secondary',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: '10%',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <HeaderButton onClick={enableAdd ? onAdd : undefined} />
          </Box>

          <Box
            sx={{
              px: 1,
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              justifyItems: 'center',
              height: '90%',
            }}
          >
            {renderEmptyIcon()}
            <Typography
              variant="h6"
              sx={{
                color: colors.text,
                fontWeight: 700,
                mb: 1,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              {emptyTitle}
            </Typography>
            {emptyLines.map((line, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  lineHeight: 1.7,
                  fontFamily: 'Rubik, sans-serif',
                  color: colors.secondaryText,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {hasSelection && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              px: 1,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ mb: '-6px', fontFamily: 'Rubik, sans-serif' }}>
                <img
                  src={MessageIcon}
                  alt="message"
                  style={{
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                  }}
                />
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  mr: 1,
                  color: colors.text,
                  fontSize: '14px',
                  fontFamily: 'Rubik, sans-serif',
                }}
              >
                הודעות
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: colors.mutedText,
                  fontSize: '14px',
                  fontFamily: 'Rubik, sans-serif',
                }}
              >
                {count}
              </Typography>
            </Box>
            <HeaderButton onClick={enableAdd ? onAdd : undefined} />
          </Box>

          <Box sx={{ px: 1, py: 0.75 }}>
            <Divider sx={{ borderColor: colors.divider }} />
          </Box>

          <Stack
            spacing={2.5}
            sx={{
              px: 1,
              pb: 1,
              pt: 0.5,
              overflowY: 'auto',
              flex: 1,
              maxHeight: '100%',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': {
                backgroundColor: colors.scrollTrack,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.scrollThumb,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: colors.scrollThumbHover,
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : items.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: colors.mutedText,
                  textAlign: 'center',
                  mt: 2,
                  fontFamily: 'Rubik, sans-serif',
                }}
              >
                לא נמצאו הודעות ללקוח זה.
              </Typography>
            ) : (
              items.map((m, idx) => (
                <Box key={m.id}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.secondaryText,
                      display: 'block',
                      fontSize: '10px',
                      fontFamily: 'Rubik, sans-serif',
                    }}
                  >
                    {m.channel}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.secondaryText,
                      display: 'block',
                      mb: 0.5,
                      fontSize: '10px',
                      fontFamily: 'Rubik, sans-serif',
                    }}
                  >
                    {m.contact}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: colors.text,
                      mb: 0.5,
                      fontSize: '16px',
                      fontFamily: 'Rubik, sans-serif',
                    }}
                  >
                    {m.sender}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-line',
                      color: colors.text,
                      lineHeight: 1.2,
                      mb: 0.75,
                      fontSize: '12px',
                      fontFamily: 'Rubik, sans-serif',
                    }}
                  >
                    {m.preview}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.mutedText,
                        fontSize: '10px',
                        fontFamily: 'Rubik, sans-serif',
                      }}
                    >
                      התקבל: {m.receivedAt}
                    </Typography>
                    {m.status && (
                      <NotificationStatusChip
                        status={m.status}
                        lastError={m.lastError}
                      />
                    )}
                  </Box>

                  {idx < items.length - 1 && (
                    <Divider sx={{ mt: 1.5, borderColor: colors.divider }} />
                  )}
                </Box>
              ))
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

export default NotificationsPanel
